/**
 * PATCH: global-script-console-integrations-migration
 *
 * LaserPecker 全局 Google 集成运行时。
 *
 * 本文件直接使用公开的 Google 产品名称，不再使用需要反查的内部缩写。
 * 它把 Source 中可见的 JSON profile 转换为 legacy 主题中的同等 vendor 行为，
 * 同时保证脚本加载 single-flight、可重复执行且不会重复注入。
 *
 * 本文件负责的 theme-owned 能力：
 * - Google Tag Manager 主、次容器。
 * - Google Analytics 4 与 Google Ads destination 配置。
 * - Google Consent Mode 与 Shopify Customer Privacy 的状态同步。
 * - Google Merchant Center Store Quality Badge。
 *
 * platform/app-owned loader 不由主题注入或接管；destination config 仍写入共享 dataLayer。
 */
;(() => {
  'use strict'

  const existingApi = window.LPGlobalIntegrations
  if (existingApi?.version === 1) {
    void existingApi.init()
    return
  }

  const profileNode = document.getElementById('lp-global-integrations-profile')
  if (!profileNode) return

  let profile
  try {
    profile = JSON.parse(profileNode.textContent)
  } catch {
    return
  }

  const googleTagManagerProfile = profile.googleTagManager ?? {}
  const googleTagProfile = profile.googleTag ?? {}
  const googleConsentModeProfile = profile.googleConsentMode ?? {}
  const googleMerchantQualityBadgeProfile = profile.googleMerchantQualityBadge

  const state = {
    configuredDestinations: new Set(),
    consentDefaulted: false,
    generation: 0, // 每轮新的 init attempt 或 destroy 递增，阻止旧异步任务覆盖新 lifecycle。
    googleTagBootstrapped: false, // `gtag('js')` 在同一页面只允许入队一次。
    gtmContainers: new Set(),
    lifecycle: 'idle',
    listenersBound: false,
    loadPromises: new Map(),
    merchantStarted: false,
    shopifyPrivacyFeatureRequested: false,
    errors: [],
    singleFlight: null,
  }

  const snapshot = () => ({
    businessStore: profile.businessStore,
    configuredDestinations: [...state.configuredDestinations],
    errors: [...state.errors],
    runtimeStoreKey: profile.runtimeStoreKey,
    state: state.lifecycle,
  })

  /**
   * 通过稳定 DOM id、审计标记或完整 URL 查找已经加载的集成脚本。
   * 下方各产品 loader 共用这一层去重保护。
   */
  const findExistingIntegrationScript = (id, integrationKey, scriptUrl) =>
    document.getElementById(id) ??
    document.querySelector(`[data-lp-global-integration="${integrationKey}"]`) ??
    document.querySelector(`script[src="${scriptUrl}"]`)

  /**
   * 通过 destination id 查找 Google tag loader。
   * Google/Shopify 可以额外添加 `cx`、`gtm` 等 query 参数，不影响匹配。
   */
  const findGoogleTagLoader = (destinationId) => {
    if (!destinationId) return null

    for (const script of document.scripts) {
      try {
        const url = new URL(script.src, document.baseURI)
        if (
          url.hostname === 'www.googletagmanager.com' &&
          url.pathname === '/gtag/js' &&
          url.searchParams.get('id') === destinationId
        ) {
          return script
        }
      } catch {
        // 忽略无法解析为 URL 的脚本地址，继续查找当前标准 destination。
      }
    }
    return null
  }

  /**
   * 通过 container id 查找 Google Tag Manager loader。
   * Tag Assistant Preview 会增加 `gtm_auth`、`gtm_preview` 等参数；这些参数不代表
   * 另一个 container，因此仍应复用已有脚本。
   */
  const findGoogleTagManagerLoader = (containerId) => {
    if (!containerId) return null

    for (const script of document.scripts) {
      try {
        const url = new URL(script.src, document.baseURI)
        if (
          url.hostname === 'www.googletagmanager.com' &&
          url.pathname === '/gtm.js' &&
          url.searchParams.get('id') === containerId
        ) {
          return script
        }
      } catch {
        // 忽略无法解析为 URL 的脚本地址，继续查找当前标准 container。
      }
    }
    return null
  }

  /**
   * 底层外部脚本 loader。
   *
   * 产品方法负责提供公开 URL、owner 与业务行为；本 helper 只负责 DOM 插入、
   * single-flight 复用，以及网络失败后的节点清理。公共 API 要发起新一轮网络重试，
   * 必须先调用 `destroy()` 清空本轮 single-flight，再调用 `init()`。
   * 对其它 owner 插入的无标记脚本，只在产品方法明确要求时等待 load/error；
   * 主题不会改写或删除该外部节点。
   */
  const loadExternalIntegrationScript = ({
    attributes = {},
    existingLoadTimeoutMs = 0,
    existingScript = null,
    id,
    integrationKey,
    scriptUrl,
    waitForUnmarkedExisting = false,
  }) => {
    if (state.loadPromises.has(integrationKey)) {
      return state.loadPromises.get(integrationKey)
    }

    let existing = existingScript ?? findExistingIntegrationScript(id, integrationKey, scriptUrl)
    if (existing?.dataset?.lpGlobalIntegrationStatus === 'failed') {
      existing.remove()
      existing = null
    }
    if (existing) {
      const existingStatus = existing.dataset?.lpGlobalIntegrationStatus
      const waitsForManagedExisting = existingStatus === 'loading'
      const shouldWaitForUnmarkedExisting = waitForUnmarkedExisting && !existingStatus

      if (waitsForManagedExisting || shouldWaitForUnmarkedExisting) {
        const pendingExisting = new Promise((resolve, reject) => {
          let timeoutId = null
          const cleanup = () => {
            existing.removeEventListener('load', handleLoad)
            existing.removeEventListener('error', handleError)
            if (timeoutId !== null) window.clearTimeout(timeoutId)
          }
          const handleLoad = () => {
            cleanup()
            if (waitsForManagedExisting) {
              existing.dataset.lpGlobalIntegrationStatus = 'loaded'
            }
            resolve(existing)
          }
          const handleError = () => {
            cleanup()
            if (waitsForManagedExisting) {
              existing.dataset.lpGlobalIntegrationStatus = 'failed'
              existing.remove()
            }
            reject(new Error(`load_failed:${integrationKey}`))
          }

          existing.addEventListener('load', handleLoad, { once: true })
          existing.addEventListener('error', handleError, { once: true })
          if (shouldWaitForUnmarkedExisting && existingLoadTimeoutMs > 0) {
            timeoutId = window.setTimeout(() => {
              cleanup()
              reject(new Error(`load_timeout:${integrationKey}`))
            }, existingLoadTimeoutMs)
          }
        }).catch((error) => {
          state.loadPromises.delete(integrationKey)
          throw error
        })
        state.loadPromises.set(integrationKey, pendingExisting)
        return pendingExisting
      }

      const reused = Promise.resolve(existing)
      state.loadPromises.set(integrationKey, reused)
      return reused
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.id = id
      script.async = true
      script.src = scriptUrl
      script.dataset.lpGlobalIntegration = integrationKey
      script.dataset.lpGlobalIntegrationStatus = 'loading'

      for (const [name, value] of Object.entries(attributes)) {
        script.setAttribute(name, value)
      }

      script.addEventListener(
        'load',
        () => {
          script.dataset.lpGlobalIntegrationStatus = 'loaded'
          resolve(script)
        },
        { once: true },
      )
      script.addEventListener(
        'error',
        () => {
          script.dataset.lpGlobalIntegrationStatus = 'failed'
          script.remove()
          reject(new Error(`load_failed:${integrationKey}`))
        },
        { once: true },
      )
      document.head.append(script)
    })

    const retryablePromise = promise.catch((error) => {
      state.loadPromises.delete(integrationKey)
      throw error
    })
    state.loadPromises.set(integrationKey, retryablePromise)
    return retryablePromise
  }

  const ensureGoogleDataLayer = () => {
    window.dataLayer = window.dataLayer || []
    return window.dataLayer
  }

  const ensureGoogleTagCommand = () => {
    const dataLayer = ensureGoogleDataLayer()
    window.gtag =
      window.gtag ||
      function gtag() {
        dataLayer.push(arguments)
      }
    return window.gtag
  }

  /**
   * 设置 Google Consent Mode 默认状态。
   *
   * legacy/vendor 等价行为：
   * `gtag('consent', 'default', { analytics_storage: 'denied', ... })`
   *
   * 归属方：theme；授权判断来源为 Shopify Customer Privacy。
   * 触发时机：启用区域的 profile 在 init 时执行一次。
   * 去重策略：`state.consentDefaulted` 保证每个页面运行时只写入一次默认指令。
   * 故障隔离：这里不加载远程脚本；Shopify API 不存在时保持默认状态。
   */
  const applyGoogleConsentModeDefault = () => {
    if (state.consentDefaulted) return

    ensureGoogleTagCommand()('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'denied',
      region: googleConsentModeProfile.regions,
      wait_for_update: 500,
    })
    state.consentDefaulted = true
  }

  /**
   * 根据 Shopify Customer Privacy 同步 Google Consent Mode。
   *
   * legacy/vendor 等价行为：
   * `gtag('consent', 'update', { analytics_storage, ad_storage, ... })`
   *
   * 归属方：theme integration；用户授权决策仍由 Shopify Customer Privacy 负责。
   * 映射关系：analytics → analytics storage，marketing → 广告相关 consent，
   * preferences → personalization storage；三者保持独立。
   * 触发时机：init、Shopify consent-tracking API ready 与
   * `visitorConsentCollected`。
   * 去重策略：每次重新读取当前授权状态，因此 update 可以安全重复。
   * 故障隔离：Shopify privacy API 尚未 ready 时不发送 update，由 readiness
   * callback 在 API 可用后补发已保存的状态。
   */
  const syncGoogleConsentModeFromShopifyPrivacy = () => {
    if (googleConsentModeProfile.enabled !== true) return true

    const privacy = window.Shopify?.customerPrivacy
    if (
      typeof privacy?.analyticsProcessingAllowed !== 'function' ||
      typeof privacy?.marketingAllowed !== 'function' ||
      typeof privacy?.preferencesProcessingAllowed !== 'function'
    ) {
      return false
    }

    const analyticsAllowed = privacy.analyticsProcessingAllowed() === true
    const marketingAllowed = privacy.marketingAllowed() === true
    const preferencesAllowed = privacy.preferencesProcessingAllowed() === true
    ensureGoogleTagCommand()('consent', 'update', {
      ad_personalization: marketingAllowed ? 'granted' : 'denied',
      ad_storage: marketingAllowed ? 'granted' : 'denied',
      ad_user_data: marketingAllowed ? 'granted' : 'denied',
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      personalization_storage: preferencesAllowed ? 'granted' : 'denied',
    })
    return true
  }

  /**
   * 等待 Shopify Customer Privacy API ready 后补发已保存的 consent 状态。
   *
   * Impact 的 `privacy-bar` 也通过 `Shopify.loadFeatures` 请求同一个官方 feature；
   * 这里复用 Shopify 的 feature 去重与 onLoad 信号，不注入第二份脚本。
   * runtime 已 destroy 时 callback 不再写入 dataLayer；之后重新 init 会再次读取 API。
   */
  const syncGoogleConsentModeWhenShopifyPrivacyIsReady = () => {
    if (syncGoogleConsentModeFromShopifyPrivacy()) return

    const shopify = window.Shopify
    if (state.shopifyPrivacyFeatureRequested || typeof shopify?.loadFeatures !== 'function') {
      return
    }

    state.shopifyPrivacyFeatureRequested = true
    try {
      shopify.loadFeatures([
        {
          name: 'consent-tracking-api',
          version: '0.1',
          onLoad: (error) => {
            state.shopifyPrivacyFeatureRequested = false
            if (error || state.lifecycle === 'destroyed') return
            syncGoogleConsentModeFromShopifyPrivacy()
          },
        },
      ])
    } catch {
      state.shopifyPrivacyFeatureRequested = false
    }
  }

  /**
   * 加载一个 Google Tag Manager container。
   *
   * legacy/vendor 等价行为：
   * 1. `dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })`
   * 2. `<script async src="https://www.googletagmanager.com/gtm.js?id=GTM-...">`
   *
   * 归属方：theme。
   * 触发时机：全局集成首次 bootstrap。
   * 去重策略：稳定 DOM id、`data-lp-global-integration`、container Set 与脚本 URL。
   * 故障隔离：被 reject 的 Promise 会记录到 `getState().errors`；
   * 其它集成仍由 `Promise.allSettled` 继续执行。
   */
  const loadGoogleTagManagerContainer = ({ containerId, placement, requiresCmpMarker = false }) => {
    if (!containerId) return Promise.resolve(null)

    const integrationKey = `gtm-${placement}`
    const scriptUrl = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
      containerId,
    )}`
    let existing =
      findExistingIntegrationScript(`lp-global-${integrationKey}`, integrationKey, scriptUrl) ??
      findGoogleTagManagerLoader(containerId)
    if (existing?.dataset?.lpGlobalIntegrationStatus === 'failed') {
      existing.remove()
      existing = null
    }
    if (existing) {
      state.gtmContainers.add(containerId)
    } else if (!state.gtmContainers.has(containerId)) {
      ensureGoogleDataLayer().push({
        'gtm.start': Date.now(),
        event: 'gtm.js',
      })
      state.gtmContainers.add(containerId)
    }

    return loadExternalIntegrationScript({
      attributes: requiresCmpMarker ? { 'data-cmp-ab': '1' } : {},
      existingScript: existing,
      id: `lp-global-${integrationKey}`,
      integrationKey,
      scriptUrl,
    }).catch((error) => {
      state.gtmContainers.delete(containerId)
      throw error
    })
  }

  const loadPrimaryGoogleTagManager = () =>
    loadGoogleTagManagerContainer({
      ...googleTagManagerProfile.primary,
      placement: 'primary',
    })

  const loadSecondaryGoogleTagManager = () =>
    loadGoogleTagManagerContainer({
      ...googleTagManagerProfile.secondary,
      placement: 'secondary',
    })

  /**
   * 配置 Google Analytics 4 与 Google Ads destinations。
   *
   * legacy/vendor 等价行为：
   * 1. `gtag('js', new Date())`
   * 2. `gtag('config', 'G-...')` and/or `gtag('config', 'AW-...')`
   * 3. loader 为 theme-owned 时加载
   *    `https://www.googletagmanager.com/gtag/js?id=<loader destination>`.
   *
   * 归属方：destination config 为 theme-owned；loader owner 由
   * `profile.googleTag.loader.owner` 声明，也可能属于 Shopify Customer Events。
   * 触发时机：Consent Mode 默认指令入队后的首次 bootstrap；theme-owned loader
   * 缺失时立即注入，不等待 `window.load`，避免慢资源或快速离站丢失首屏信号。
   * 去重策略：destination Set 加按 destination 查找 loader。
   * 故障隔离：loader 失败只记录错误，不阻断 GTM 或 Merchant。
   */
  const configureGoogleAnalyticsAndAdsDestinations = async () => {
    const destinations = [
      ...(googleTagProfile.analyticsDestinations ?? []),
      ...(googleTagProfile.adsDestinations ?? []),
    ].filter(Boolean)
    if (destinations.length === 0) return null

    const googleTag = ensureGoogleTagCommand()
    if (!state.googleTagBootstrapped) {
      googleTag('js', new Date())
      state.googleTagBootstrapped = true
    }
    for (const destination of destinations) {
      if (state.configuredDestinations.has(destination)) continue
      googleTag('config', destination)
      state.configuredDestinations.add(destination)
    }

    const loader = googleTagProfile.loader ?? {}
    if (loader.owner !== 'theme') return null

    const existingLoader = findGoogleTagLoader(loader.destinationId)

    return loadExternalIntegrationScript({
      attributes:
        googleTagManagerProfile.secondary?.requiresCmpMarker === true ? { 'data-cmp-ab': '1' } : {},
      existingScript: existingLoader,
      id: 'lp-global-gtag',
      integrationKey: 'gtag',
      scriptUrl: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        loader.destinationId,
      )}`,
    })
  }

  /**
   * 加载 Google Merchant Center Store Quality Badge。
   *
   * legacy/vendor 等价行为：
   * 1. `<script src="https://www.gstatic.com/shopping/merchant/merchantwidget.js">`
   * 2. `merchantwidget.start()` 使用 profile 声明的位置与边距。
   *
   * 归属方：theme；启用店铺由 Source 中可见的 profile 声明。
   * 触发时机：首次 bootstrap。
   * 去重策略：每个页面运行时只创建一个脚本 Promise，并只调用一次
   * `merchantwidget.start()`。若其它 owner 已插入无状态标记的同 URL 脚本且 API
   * 尚未出现，最多等待 5 秒的 load/error，不重复注入或接管该节点。
   * 故障隔离：loader/API 不可用时记录错误，GTM 与 Google tag 继续执行。
   */
  const loadGoogleMerchantQualityBadge = async () => {
    if (!googleMerchantQualityBadgeProfile || state.merchantStarted) return null

    const script = await loadExternalIntegrationScript({
      id: 'lp-global-merchant-widget',
      integrationKey: 'merchant-widget',
      scriptUrl: googleMerchantQualityBadgeProfile.scriptUrl,
      existingLoadTimeoutMs: 5000,
      waitForUnmarkedExisting: typeof window.merchantwidget?.start !== 'function',
    })
    if (state.merchantStarted) return script
    if (typeof window.merchantwidget?.start !== 'function') {
      throw new Error('merchant_widget_unavailable')
    }

    window.merchantwidget.start({
      position: googleMerchantQualityBadgeProfile.position,
      mobileSideMargin: googleMerchantQualityBadgeProfile.mobileSideMargin,
      mobileBottomMargin: googleMerchantQualityBadgeProfile.mobileBottomMargin,
    })
    state.merchantStarted = true
    return script
  }

  const onShopifyVisitorConsentCollected = () => syncGoogleConsentModeWhenShopifyPrivacyIsReady()

  const bindShopifyCustomerPrivacyListener = () => {
    if (state.listenersBound) return
    document.addEventListener('visitorConsentCollected', onShopifyVisitorConsentCollected)
    state.listenersBound = true
  }

  const unbindShopifyCustomerPrivacyListener = () => {
    if (!state.listenersBound) return
    document.removeEventListener('visitorConsentCollected', onShopifyVisitorConsentCollected)
    state.listenersBound = false
  }

  const api = {
    version: 1,
    // 重新请求失败的 vendor 脚本时使用 `destroy()` → `init()`；直接重复 init 会复用本轮 Promise。
    destroy() {
      state.generation += 1
      unbindShopifyCustomerPrivacyListener()
      state.lifecycle = 'destroyed'
      state.singleFlight = null
      return snapshot()
    },
    getState: snapshot,
    init() {
      if (state.singleFlight) return state.singleFlight

      const generation = ++state.generation
      state.lifecycle = 'initializing'
      state.errors = []
      bindShopifyCustomerPrivacyListener()
      if (googleConsentModeProfile.enabled === true) {
        applyGoogleConsentModeDefault()
        syncGoogleConsentModeWhenShopifyPrivacyIsReady()
      }

      // 保持启动清单显式可见，开发者可以直接审计这里会启动哪些集成。
      const integrationTasks = [
        loadPrimaryGoogleTagManager(),
        loadSecondaryGoogleTagManager(),
        configureGoogleAnalyticsAndAdsDestinations(),
        loadGoogleMerchantQualityBadge(),
      ]

      state.singleFlight = Promise.allSettled(integrationTasks).then((results) => {
        if (generation !== state.generation || state.lifecycle === 'destroyed') {
          return snapshot()
        }
        state.errors = results
          .filter(({ status }) => status === 'rejected')
          .map(({ reason }) => reason?.message ?? 'integration_failed')
        state.lifecycle = 'ready'
        return snapshot()
      })

      return state.singleFlight
    },
  }

  window.__lpGlobalIntegrations = state
  window.LPGlobalIntegrations = api
  void api.init()
})()
